import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Gate } from '../../../global/contexts/auth/auth';
import { IconEdit, IconPlus } from '../../../common/icons';
import { useTemplatesList } from '../contexts/notifications';
import { templateItems, templateTotal, isArchived, CHANNEL_LABEL } from '../utils/format';
import SectionTabs from '../../navigation/section-tabs';
import { SERVICE_TABS } from '../../navigation/tabs';

import '../../../common/styles/catalog.css';
import '../styles/notifications.css';

export default function NotificationsOverview() {
    const [search, setSearch] = useState('');
    const { data, isLoading, isFetching, error } = useTemplatesList();

    const all = useMemo(() => templateItems(data), [data]);
    const total = templateTotal(data, all.length);

    // El contrato de list no documenta búsqueda `q` → filtramos en cliente por código/nombre/trigger.
    const templates = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return all;
        return all.filter((t) =>
            [t.code, t.name, t.trigger].filter(Boolean).some((v) => String(v).toLowerCase().includes(q)),
        );
    }, [all, search]);

    return (
        <div className="cat-overview">
            <SectionTabs tabs={SERVICE_TABS} />

            <header className="cat-overview__header">
                <div className="cat-overview__heading">
                    <h1 className="cat-overview__title">Plantillas</h1>
                    <span className="cat-overview__count">{total}</span>
                </div>
                <Gate domain="notifications" action="write">
                    <Link to="/admin/notifications/new" className="cat-overview__add">
                        <IconPlus />
                        <span>Nueva plantilla</span>
                    </Link>
                </Gate>
            </header>

            <p className="cat-overview__note">
                El cuerpo del mensaje vive aprobado en Twilio/Meta. Acá gestionás el registro: código, canal,
                trigger, variables y el Content SID (HX).
            </p>

            <div className="cat-overview__toolbar">
                <div className="cat-overview__search">
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Buscar por código, nombre o trigger…"
                    />
                </div>
            </div>

            {error && <div className="cat-overview__error">{error.message || 'No se pudieron cargar las plantillas'}</div>}

            {isLoading ? (
                <div className="cat-overview__state">Cargando plantillas…</div>
            ) : !templates.length ? (
                <div className="cat-overview__state">
                    <p className="cat-overview__state-text">
                        {search ? 'Ninguna plantilla coincide con la búsqueda.' : 'Aún no hay plantillas.'}
                    </p>
                    {!search && (
                        <Gate domain="notifications" action="write">
                            <Link to="/admin/notifications/new" className="cat-overview__add">
                                <IconPlus /><span>Crear la primera plantilla</span>
                            </Link>
                        </Gate>
                    )}
                </div>
            ) : (
                <div className={`cat-overview__card ${isFetching ? 'cat-overview__card--busy' : ''}`}>
                    <table className="cat-table">
                        <thead>
                            <tr>
                                <th>Código</th>
                                <th>Nombre</th>
                                <th>Canal</th>
                                <th>Trigger</th>
                                <th>Estado</th>
                                <th className="cat-table__actions-col" aria-label="Acciones" />
                            </tr>
                        </thead>
                        <tbody>
                            {templates.map((t) => {
                                const archived = isArchived(t);
                                return (
                                    <tr key={t.code} className="cat-table__row">
                                        <td>
                                            <Gate
                                                domain="notifications"
                                                action="write"
                                                fallback={<span className="ntf-code">{t.code}</span>}
                                            >
                                                <Link to={`/admin/notifications/${encodeURIComponent(t.code)}/edit`} className="cat-table__name ntf-code">
                                                    {t.code}
                                                </Link>
                                            </Gate>
                                        </td>
                                        <td>{t.name || <span className="cat-table__empty">—</span>}</td>
                                        <td>{CHANNEL_LABEL[t.channel] || t.channel || <span className="cat-table__empty">—</span>}</td>
                                        <td>{t.trigger || <span className="cat-table__empty">—</span>}</td>
                                        <td>
                                            <span className={`cat-badge ${archived ? 'cat-badge--0' : 'cat-badge--2'}`}>
                                                {archived ? 'Archivada' : 'Activa'}
                                            </span>
                                        </td>
                                        <td className="cat-table__actions">
                                            <Gate domain="notifications" action="write">
                                                <Link
                                                    to={`/admin/notifications/${encodeURIComponent(t.code)}/edit`}
                                                    className="cat-table__edit"
                                                    aria-label={`Editar ${t.code}`}
                                                    title="Editar"
                                                >
                                                    <IconEdit />
                                                </Link>
                                            </Gate>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
